import {FC, useState, useCallback} from 'react';

import {Input} from '@shared_web/components/core/input';
import {hideModal} from '@shared_web/components/core/modal';
import {showAlert, showSuccess} from '@shared_web/components/core/notifications';
import {notifyError} from '@shared_web/lib/notification';

import {GenericModalForm} from '@mavismart_frontend/components/generic_modal_form';
import {translate} from '@mavismart_frontend/components/translate';
import {apiCall} from '@mavismart_frontend/lib/api';
import {INPUT_THEME, INPUT_THEME_ERROR} from '@mavismart_frontend/lib/constants';
import {getUserLanguage, useAccount} from '@mavismart_frontend/stores/account_store';

interface CollaboratorFormProps {
  groupId: string;
  fetchCollaborators: () => Promise<void>;
}

export const CollaboratorForm: FC<CollaboratorFormProps> = ({groupId, fetchCollaborators}) => {
  const [email, setEmail] = useState('');
  const account = useAccount();
  const [isLoading, setIsLoading] = useState(false);
  const [hasError, setHasError] = useState(false);

  const handleSubmit = useCallback(async () => {
    if (email) {
      setHasError(false);
      try {
        setIsLoading(true);
        await apiCall('POST /add-collaborator', {email, groupId});
        showSuccess(translate('collaborator_added', getUserLanguage(account)));
      } catch (err) {
        notifyError(err);
        setHasError(true);
      } finally {
        setEmail('');
        hideModal();
        setIsLoading(false);
        fetchCollaborators().catch(notifyError);
      }
    } else {
      setHasError(true);
      showAlert(translate('all_fields_required', getUserLanguage(account)));
    }
  }, [email, account, fetchCollaborators, groupId]);

  return (
    <GenericModalForm
      onSubmit={handleSubmit}
      loading={isLoading}
      childrenInput={
        <Input
          width={309}
          type="email"
          autoComplete="email"
          value={email}
          syncState={setEmail}
          placeholder={translate('collaborator_s_email', getUserLanguage(account))}
          themeOverrides={
            hasError
              ? {
                  input: {
                    ...INPUT_THEME_ERROR,
                  },
                }
              : {input: {...INPUT_THEME}}
          }
        />
      }
    />
  );
};

CollaboratorForm.displayName = 'CollaboratorForm';
